import { useState } from "react";
import { motion } from "framer-motion";
import { Loader2, Send, Mail, MessageSquare, ShieldCheck } from "lucide-react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { useLang } from "../context/LanguageContext";
import api, { formatApiErrorDetail } from "../lib/api";

export default function Contact() {
  const { t } = useLang();
  const c = t.contact;
  const [topic, setTopic] = useState(c.topics[0].value);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  const [hp, setHp] = useState(""); // honeypot
  const [challenge, setChallenge] = useState(() => ({ a: 2 + Math.floor(Math.random() * 7), b: 1 + Math.floor(Math.random() * 8) }));
  const [answer, setAnswer] = useState("");
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState(null); // {type, text}

  const submit = async (e) => {
    e.preventDefault();
    setStatus(null);
    if (parseInt(answer, 10) !== challenge.a + challenge.b) {
      setStatus({ type: "err", text: c.humanError });
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/contact", { name, email, topic, message, hp });
      setStatus({ type: "ok", text: data.emailed ? c.success : c.stored });
      setName("");
      setEmail("");
      setMessage("");
      setAnswer("");
      setTopic(c.topics[0].value);
      setChallenge({ a: 2 + Math.floor(Math.random() * 7), b: 1 + Math.floor(Math.random() * 8) });
    } catch (err) {
      setStatus({ type: "err", text: formatApiErrorDetail(err.response?.data?.detail) || c.error });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="App overflow-x-hidden bg-u-bg text-u-ink min-h-screen">
      <Navbar />
      <section className="relative pt-36 pb-24">
        <div className="mesh-glow w-[500px] h-[500px] bg-u-purple/20 -top-20 -left-20" />
        <div className="mesh-glow w-[450px] h-[450px] bg-blue-400/20 top-40 right-0" />

        <div className="relative mx-auto max-w-3xl px-5 sm:px-8">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-10"
          >
            <p className="text-xs font-semibold uppercase tracking-[0.25em] text-u-purple mb-4">{c.overline}</p>
            <h1 className="font-display font-bold text-4xl md:text-5xl tracking-tight text-u-ink">{c.title}</h1>
            <p className="mt-5 text-lg text-slate-500 max-w-xl mx-auto">{c.subtitle}</p>
          </motion.div>

          <motion.form
            onSubmit={submit}
            data-testid="contact-form"
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="rounded-[32px] card p-8 sm:p-10 space-y-6"
          >
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-3">{c.topicLabel}</label>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                {c.topics.map((tp) => (
                  <button
                    type="button"
                    key={tp.value}
                    data-testid={`contact-topic-${tp.value.replace(/[^a-zA-Z0-9]/g, "-")}`}
                    onClick={() => setTopic(tp.value)}
                    className={`rounded-xl border px-4 py-3 text-sm font-medium text-left transition-all ${
                      topic === tp.value
                        ? "border-u-purple bg-u-purple/8 text-u-ink ring-2 ring-u-purple/20"
                        : "border-slate-200 bg-u-bg text-slate-600 hover:border-slate-300"
                    }`}
                  >
                    {tp.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="grid sm:grid-cols-2 gap-5">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">{c.name}</label>
                <input
                  type="text" required value={name} onChange={(e) => setName(e.target.value)}
                  data-testid="contact-name"
                  className="w-full rounded-xl bg-u-bg border border-slate-200 px-4 py-3 text-u-ink placeholder-slate-400 outline-none focus:border-u-purple/60 focus:bg-white focus:ring-2 focus:ring-u-purple/15 transition-all"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">{c.email}</label>
                <input
                  type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                  data-testid="contact-email"
                  className="w-full rounded-xl bg-u-bg border border-slate-200 px-4 py-3 text-u-ink placeholder-slate-400 outline-none focus:border-u-purple/60 focus:bg-white focus:ring-2 focus:ring-u-purple/15 transition-all"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">{c.message}</label>
              <textarea
                required rows={5} value={message} onChange={(e) => setMessage(e.target.value)}
                data-testid="contact-message"
                className="w-full rounded-xl bg-u-bg border border-slate-200 px-4 py-3 text-u-ink placeholder-slate-400 outline-none focus:border-u-purple/60 focus:bg-white focus:ring-2 focus:ring-u-purple/15 transition-all resize-none"
              />
            </div>

            {/* Honeypot — hidden from humans, bots tend to fill it */}
            <input
              type="text"
              tabIndex={-1}
              autoComplete="off"
              value={hp}
              onChange={(e) => setHp(e.target.value)}
              data-testid="contact-hp"
              aria-hidden="true"
              style={{ position: "absolute", left: "-9999px", width: 1, height: 1, opacity: 0 }}
            />

            {/* Human verification */}
            <div className="rounded-xl bg-u-bg border border-slate-200 p-4">
              <label className="block text-sm font-medium text-slate-700 mb-2 flex items-center gap-2">
                <ShieldCheck size={15} className="text-u-purple" /> {c.humanLabel}
              </label>
              <div className="flex items-center gap-3">
                <span className="font-display text-lg text-u-ink whitespace-nowrap" data-testid="contact-challenge">
                  {c.humanQuestion} {challenge.a} + {challenge.b}?
                </span>
                <input
                  type="number"
                  required
                  value={answer}
                  onChange={(e) => setAnswer(e.target.value)}
                  placeholder={c.humanPlaceholder}
                  data-testid="contact-human-answer"
                  className="w-28 rounded-xl bg-white border border-slate-200 px-4 py-2.5 text-u-ink placeholder-slate-400 outline-none focus:border-u-purple/60 focus:ring-2 focus:ring-u-purple/15 transition-all"
                />
              </div>
            </div>

            <p className="flex items-center gap-2 text-xs text-slate-400">
              <Mail size={13} /> {c.ccNote}
            </p>

            {status && (
              <p
                data-testid="contact-status"
                className={`text-sm rounded-xl px-4 py-3 border ${
                  status.type === "ok"
                    ? "text-emerald-700 bg-emerald-50 border-emerald-200"
                    : "text-u-red bg-u-red/10 border-u-red/20"
                }`}
              >
                {status.text}
              </p>
            )}

            <button
              type="submit"
              disabled={loading}
              data-testid="contact-submit"
              className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-u-ink px-6 py-3.5 font-semibold text-white hover:bg-u-ink/90 transition-all disabled:opacity-60"
            >
              {loading ? <Loader2 size={18} className="animate-spin" /> : <Send size={17} />}
              {loading ? c.sending : c.send}
            </button>
          </motion.form>

          <p className="mt-6 text-center text-sm text-slate-400 flex items-center justify-center gap-2">
            <MessageSquare size={14} /> info@use1id.com
          </p>
        </div>
      </section>
      <Footer />
    </div>
  );
}
